// This file is part of re-motion
// Copyright (c) rubicon IT GmbH, www.rubicon.eu
// 
import { LogManager } from "@lib/logging/LogManager";
import { RawMessage } from "@lib/messaging/RawMessage";
import { RawMessageSerializer } from "@lib/messaging/RawMessageSerializer";
import { Socket } from "@lib/messaging/Socket";
import { EventSocket } from "@lib/messaging/sockets/EventSocket";
import { PortSocket } from "@lib/messaging/sockets/PortSocket";
import { Assert } from "@lib/utilities/Assert";
import { EventListener } from "@lib/utilities/EventListener";
import { IEventListener } from "@lib/utilities/IEventListener";
import { Random } from "@lib/utilities/Random";

const log = LogManager.createLog("SocketManager");

/**
 * Provides methods for handling multiple sockets.
 * Handles the receiving, sending and disconnects of registered sockets.
 */
export class SocketManager
{
  private readonly _onDisconnect = new EventListener<(socket: Socket) => void>();
  private readonly _onMessage = new EventListener<(socket: Socket, id: string, rawMessage: RawMessage) => void>();
  private readonly rawMessageSerializer = new RawMessageSerializer();
  private readonly sockets: Dictionary<EventSocket | PortSocket> = {};

  private _connectedSockets = 0;

  get connectedSockets(): number
  {
    return this._connectedSockets;
  }

  get onDisconnect(): IEventListener<(socket: Socket) => void>
  {
    return this._onDisconnect;
  }

  get onMessage(): IEventListener<(socket: Socket, id: string, rawMessage: RawMessage) => void>
  {
    return this._onMessage;
  }

  add(socket: EventSocket | PortSocket, requiredID?: string): string
  {
    const id = requiredID || Random.generateSocketID();
    if (id in this.sockets)
      throw new Error("A socket with the specified id already exists.");

    if (socket instanceof EventSocket)
    {
      socket.onMessage.addListener((message) => this.handleMessage(id, message));
    }
    else if (socket instanceof PortSocket)
    {
      socket.onDisconnect.addListener(() => this.handleDisconnect(id));
      socket.onMessage.addListener((message) => this.handleMessage(id, message));
    }
    else
    {
      Assert.ensureNever(socket);
    }

    this.sockets[id] = socket;
    this._connectedSockets++;
    return id;
  }

  get(id: string): Nullable<EventSocket | PortSocket>
  {
    return this.sockets[id] || null;
  }

  sendRawMessage(id: string, rawMessage: RawMessage): void
  {
    const socket = this.sockets[id];
    if (!socket)
    {
      log.error("Could not find the socket for '%s'", id);
      return;
    }

    rawMessage.destination = rawMessage.destination.withoutNetworkIdentifier();

    const message = this.rawMessageSerializer.serialize(rawMessage);
    socket.sendMessage(message);
  }

  private handleDisconnect(id: string)
  {
    const socket = this.sockets[id];
    if (socket)
    {
      delete this.sockets[id];
      this._onDisconnect.dispatch(socket);
    }
  }

  private handleMessage(id: string, message: object)
  {
    const rawMessage = this.rawMessageSerializer.deserialize(message);
    if (!rawMessage)
    {
      log.error("Dropped received raw message from '%s': invalid format (%o)", id, message);
      return;
    }

    if (rawMessage.source.networkIdentifier)
    {
      log.error("Dropped received raw message from '%s': invalid source version (%o)", id, rawMessage);
      return;
    }

    rawMessage.source = rawMessage.source.withNetworkIdentifier(id);

    const socket = this.sockets[id];
    if (socket)
      this._onMessage.dispatch(socket, id, rawMessage);
  }
}